import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import { Card, LinearProgress, Table, TableContainer } from "@mui/material";

import { useAppDispatch } from "src/redux/hooks";
import { setClearAllErrors } from "src/redux/slices/ordering/orderSetSlice";

import { Order } from "@models/Order";

import OrderSetHead from "./OrderSetHead";
import OrderSetOrders from "./OrderSetOrders";
import "./OrderSetTable.css";
import { useCurrentOrderSet } from "./data/CurrentOrderSetContext";

const formValuesFromOrders = (orders: Order[]): Record<string, string> => {
  const formValues = orders.reduce((acc, order) => {
    order.orderVariants.forEach((ov) => {
      acc[ov.id] = String(ov.qty);
    });
    return acc;
  }, {});

  return formValues;
};

const OrderSetTable = () => {
  const dispatch = useAppDispatch();
  const clearAllErrors = useSelector(
    (state: any) => state.orderSet.clearAllErrors
  );
  const { isFetching, orders } = useCurrentOrderSet();
  const skipFirstResetRef = useRef(true);
  const formMethods = useForm<Record<string, any>>({
    defaultValues: formValuesFromOrders(orders),
  });
  const { reset } = formMethods;
  const [stateCodeFilter, setStateCodeFilter] = useState<null | string>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  // https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  const measuredRef = useCallback((node: HTMLElement | null) => {
    if (node !== null) {
      setHeaderHeight(node.getBoundingClientRect().height);
    }
  }, []);
  useEffect(() => {
    if (isFetching) return;
    if (skipFirstResetRef.current) {
      skipFirstResetRef.current = false;
      return;
    }
    reset(formValuesFromOrders(orders), { keepDirtyValues: true });
  }, [isFetching, orders, reset]);

  useEffect(() => {
    if (clearAllErrors) {
      reset();
      dispatch(setClearAllErrors(false));
    }
  }, [clearAllErrors, reset, dispatch]);
  return (
    <div className="pb-20 transition-opacity relative">
      {isFetching && (
        <LinearProgress className="absolute inset-0 bottom-auto" />
      )}
      <Card
        style={{ opacity: isFetching ? 0.5 : 1 }}
        className="shadow-md overflow-hidden"
      >
        <TableContainer
          className="order-set-table-container"
          style={
            {
              "--table-head-height": `${headerHeight}px`,
            } as CSSProperties & { "--table-head-height": string }
          }
        >
          <Table size="small">
            <OrderSetHead
              measuredRef={measuredRef}
              stateCodeFilter={stateCodeFilter}
              setStateCodeFilter={setStateCodeFilter}
            />
            <FormProvider {...formMethods}>
              <OrderSetOrders stateCodeFilter={stateCodeFilter} />
            </FormProvider>
          </Table>
        </TableContainer>
      </Card>
    </div>
  );
};

export default OrderSetTable;
